import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors } from '@/theme/colors';
import { formatDate } from '../../../../utils';
interface DatePickerCellProps {
  value: string | number | null | undefined;
  onSave?: (newValue: string) => Promise<void> | void;
  enableEditing?: boolean;
  disabled?: boolean;
  fontSize?: string;
  showIcon?: boolean;
  iconSize?: string;
  placeholder?: string;
  ariaLabel?: string;
  variant?: 'inline' | 'boxed';
  displayFormat?: 'iso' | 'locale';
  nativePickerAnchor?: 'left' | 'right';
  nativePickerInset?: {
    top?: number | string;
    right?: number | string;
    bottom?: number | string;
    left?: number | string;
  };
}

type InputWithPicker = HTMLInputElement & {
  showPicker?: () => void;
};

const normalizeDateValue = (value: DatePickerCellProps['value']): string => {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'number') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  }

  const strValue = String(value);
  if (strValue.length >= 10) return strValue.slice(0, 10);
  return strValue;
};

const LazyDatePickerCellComponent: React.FC<DatePickerCellProps> = ({
  value,
  onSave,
  enableEditing = true,
  disabled = false,
  fontSize = '1.4rem',
  showIcon = true,
  iconSize = '1.8rem',
  placeholder = 'Add Date',
  ariaLabel = 'Date',
  variant = 'inline',
  displayFormat = 'iso',
  nativePickerAnchor,
  nativePickerInset,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizedValue = normalizeDateValue(value);
  const [localValue, setLocalValue] = useState<string>(normalizedValue);

  useEffect(() => {
    setLocalValue(normalizedValue);
  }, [normalizedValue]);

  const displayText = useMemo(() => {
    if (!localValue) return variant === 'boxed' ? 'N/A' : '';
    if (displayFormat === 'iso') return localValue;

    const date = new Date(localValue);
    if (Number.isNaN(date.getTime())) return variant === 'boxed' ? 'N/A' : localValue;
    return formatDate(date); // import from utils
  }, [displayFormat, localValue, variant]);

  const handleIconClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();

      if (disabled) return;

      const input = inputRef.current as InputWithPicker | null;
      if (!input) return;

      if (typeof input.showPicker === 'function') {
        input.showPicker();
        return;
      }

      input.focus();
      input.click();
    },
    [disabled]
  );

  const handleDateChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      const newValue = e.target.value;
      const previousValue = localValue;
      setLocalValue(newValue);

      if (!onSave) return;

      try {
        await Promise.resolve(onSave(newValue));
      } catch (err) {
        console.error('Failed to save date', err);
        setLocalValue(previousValue);
      }
    },
    [disabled, localValue, onSave]
  );

  if (!enableEditing) {
    return (
      <Typography fontSize={fontSize} fontWeight={400} color={abyssColors.primary[300]}>
        {displayText || 'N/A'}
      </Typography>
    );
  }

  if (variant === 'boxed') {
    const handleBoxClick = () => {
      if (disabled) return;

      const input = inputRef.current as InputWithPicker | null;
      if (!input) return;

      if (typeof input.showPicker === 'function') {
        input.showPicker();
        return;
      }

      input.focus();
      input.click();
    };

    return (
      <Box sx={{ position: 'relative', minWidth: '100px', width: '100%' }}>
        <Box
          onClick={handleBoxClick}
          sx={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            padding: '8px 12px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            fontSize,
            height: '2.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            backgroundColor: '#fff',
            '&:hover': {
              borderColor: disabled ? 'rgba(0, 0, 0, 0.23)' : 'rgba(0, 0, 0, 0.87)',
            },
          }}
        >
          {displayText}
        </Box>
        <input
          ref={inputRef}
          type="date"
          value={localValue}
          disabled={disabled}
          onChange={handleDateChange}
          style={{
            position: 'absolute',
            top: 0,
            ...(nativePickerAnchor
              ? nativePickerAnchor === 'left'
                ? { left: 0 }
                : { right: 0 }
              : { right: '20rem' }),
            width: '1px',
            height: '100%',
            opacity: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            pointerEvents: 'none',
            ...nativePickerInset,
          }}
          aria-label={ariaLabel}
        />
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ position: 'relative', width: '100%' }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {displayText ? (
        <Typography sx={{ fontSize, color: abyssColors.primary[500] }}>{displayText}</Typography>
      ) : (
        <Typography sx={{ fontSize, color: '#7E9BBC' }}>{placeholder}</Typography>
      )}
      {showIcon && (
        <IconButton
          sx={{ p: '2px' }}
          onClick={handleIconClick}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={disabled}
        >
          <AbyssIcon iconName="enhanced-calender" style={{ width: iconSize, height: iconSize }} />
        </IconButton>
      )}
      <input
        ref={inputRef}
        type="date"
        value={localValue}
        disabled={disabled}
        onChange={handleDateChange}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: '50%',
          ...(nativePickerAnchor === 'left' ? { left: 0 } : { right: 0 }),
          transform: 'translateY(-50%)',
          opacity: 0,
          pointerEvents: 'none',
          ...nativePickerInset,
        }}
        aria-label={ariaLabel}
      />
    </Stack>
  );
};

const areEqual = (prevProps: DatePickerCellProps, nextProps: DatePickerCellProps) =>
  prevProps.value === nextProps.value &&
  prevProps.onSave === nextProps.onSave &&
  prevProps.enableEditing === nextProps.enableEditing &&
  prevProps.disabled === nextProps.disabled &&
  prevProps.fontSize === nextProps.fontSize &&
  prevProps.showIcon === nextProps.showIcon &&
  prevProps.iconSize === nextProps.iconSize &&
  prevProps.placeholder === nextProps.placeholder &&
  prevProps.ariaLabel === nextProps.ariaLabel &&
  prevProps.variant === nextProps.variant &&
  prevProps.displayFormat === nextProps.displayFormat &&
  prevProps.nativePickerAnchor === nextProps.nativePickerAnchor &&
  prevProps.nativePickerInset?.top === nextProps.nativePickerInset?.top &&
  prevProps.nativePickerInset?.right === nextProps.nativePickerInset?.right &&
  prevProps.nativePickerInset?.bottom === nextProps.nativePickerInset?.bottom &&
  prevProps.nativePickerInset?.left === nextProps.nativePickerInset?.left;

export const LazyDatePickerCell = memo(LazyDatePickerCellComponent, areEqual);
LazyDatePickerCell.displayName = 'LazyDatePickerCell';
